/**
 * PageBuilder V3 Vuex Module
 *
 * Manages the HTML-first page structure where each section stores raw HTML.
 * No block abstraction - HTML is the source of truth for 100% fidelity.
 */

import { v4 as uuidv4 } from 'uuid'

const state = {
	// Array of HTML sections - each section is self-contained
	sections: [],

	// Global CSS styles (CSS variables, body styles, noise overlays)
	globalCss: '',

	// Global JavaScript (scroll progress, parallax, intersection observers)
	globalJs: '',

	// Google Fonts used across the page
	fonts: [],

	// Currently selected section for editing
	activeSectionId: null,

	// Global page metadata
	pageMetadata: {
		title: '',
		description: '',
		createdAt: null,
		updatedAt: null
	},

	// Generation state
	isGenerating: false,
	generationError: null,

	// Unsaved changes tracking
	unsavedChanges: false
}

const mutations = {
	SET_SECTIONS(state, sections) {
		state.sections = sections
	},

	SET_GLOBAL_CSS(state, css) {
		state.globalCss = css || ''
	},

	SET_GLOBAL_JS(state, js) {
		state.globalJs = js || ''
	},

	SET_FONTS(state, fonts) {
		state.fonts = fonts || []
	},

	ADD_SECTION(state, { section, afterId = null }) {
		const newSection = {
			id: section.id || uuidv4(),
			html: section.html || '',
			css: section.css || '',
			js: section.js || '',
			conversation: section.conversation || [],
			metadata: {
				generatedAt: section.metadata?.generatedAt || new Date().toISOString(),
				lastRefinedAt: section.metadata?.lastRefinedAt || null
			}
		}

		if (afterId) {
			const index = state.sections.findIndex((s) => s.id === afterId)
			if (index !== -1) {
				state.sections.splice(index + 1, 0, newSection)
			} else {
				state.sections.push(newSection)
			}
		} else {
			state.sections.push(newSection)
		}

		state.unsavedChanges = true
	},

	INSERT_SECTION(state, { afterId, section }) {
		const index = afterId ? state.sections.findIndex((s) => s.id === afterId) : -1

		const newSection = {
			id: section.id || uuidv4(),
			html: section.html || '',
			css: section.css || '',
			js: section.js || '',
			conversation: section.conversation || [],
			metadata: {
				generatedAt: new Date().toISOString(),
				lastRefinedAt: null,
				...section.metadata
			}
		}

		if (index !== -1) {
			state.sections.splice(index + 1, 0, newSection)
		} else {
			state.sections.push(newSection)
		}

		state.unsavedChanges = true
	},

	UPDATE_SECTION(state, { id, html, css, js }) {
		const section = state.sections.find((s) => s.id === id)
		if (section) {
			if (html !== undefined) section.html = html
			if (css !== undefined) section.css = css
			if (js !== undefined) section.js = js
			section.metadata.lastRefinedAt = new Date().toISOString()
			state.unsavedChanges = true
		}
	},

	UPDATE_SECTION_HTML(state, { id, html }) {
		const section = state.sections.find((s) => s.id === id)
		if (section) {
			section.html = html
			state.unsavedChanges = true
		}
	},

	DELETE_SECTION(state, sectionId) {
		const index = state.sections.findIndex((s) => s.id === sectionId)
		if (index !== -1) {
			state.sections.splice(index, 1)
			state.unsavedChanges = true

			// Clear active section if it was deleted
			if (state.activeSectionId === sectionId) {
				state.activeSectionId = null
			}
		}
	},

	REORDER_SECTIONS(state, { fromIndex, toIndex }) {
		const [section] = state.sections.splice(fromIndex, 1)
		state.sections.splice(toIndex, 0, section)
		state.unsavedChanges = true
	},

	MOVE_SECTION_UP(state, sectionId) {
		const index = state.sections.findIndex((s) => s.id === sectionId)
		if (index > 0) {
			const [section] = state.sections.splice(index, 1)
			state.sections.splice(index - 1, 0, section)
			state.unsavedChanges = true
		}
	},

	MOVE_SECTION_DOWN(state, sectionId) {
		const index = state.sections.findIndex((s) => s.id === sectionId)
		if (index !== -1 && index < state.sections.length - 1) {
			const [section] = state.sections.splice(index, 1)
			state.sections.splice(index + 1, 0, section)
			state.unsavedChanges = true
		}
	},

	ADD_TO_CONVERSATION(state, { sectionId, messages }) {
		const section = state.sections.find((s) => s.id === sectionId)
		if (section) {
			section.conversation.push(...messages)
		}
	},

	SET_ACTIVE_SECTION(state, sectionId) {
		state.activeSectionId = sectionId
	},

	SET_PAGE_METADATA(state, metadata) {
		state.pageMetadata = { ...state.pageMetadata, ...metadata }
	},

	SET_IS_GENERATING(state, value) {
		state.isGenerating = value
	},

	SET_GENERATION_ERROR(state, error) {
		state.generationError = error
	},

	SET_UNSAVED_CHANGES(state, value) {
		state.unsavedChanges = value
	},

	CLEAR_ALL(state) {
		state.sections = []
		state.globalCss = ''
		state.globalJs = ''
		state.fonts = []
		state.activeSectionId = null
		state.pageMetadata = {
			title: '',
			description: '',
			createdAt: null,
			updatedAt: null
		}
		state.isGenerating = false
		state.generationError = null
		state.unsavedChanges = false
	}
}

const actions = {
	/**
	 * Initialize the store with page data
	 */
	initializePage({ commit }, { sections = [], globalCss = '', globalJs = '', fonts = [], metadata = {} }) {
		commit('SET_SECTIONS', sections)
		commit('SET_GLOBAL_CSS', globalCss)
		commit('SET_GLOBAL_JS', globalJs)
		commit('SET_FONTS', fonts)
		commit('SET_PAGE_METADATA', {
			...metadata,
			updatedAt: new Date().toISOString()
		})
		commit('SET_UNSAVED_CHANGES', false)
	},

	/**
	 * Add a new section
	 */
	addSection({ commit }, { section, afterId = null }) {
		commit('ADD_SECTION', { section, afterId })
	},

	/**
	 * Insert a section after a specific section
	 */
	insertSection({ commit }, { afterId, section }) {
		commit('INSERT_SECTION', { afterId, section })
	},

	/**
	 * Update a section's HTML/CSS/JS
	 */
	updateSection({ commit }, { id, html, css, js }) {
		commit('UPDATE_SECTION', { id, html, css, js })
	},

	/**
	 * Update only the HTML of a section (for inline edits)
	 */
	updateSectionHTML({ commit }, { id, html }) {
		commit('UPDATE_SECTION_HTML', { id, html })
	},

	/**
	 * Set global CSS
	 */
	setGlobalCss({ commit }, css) {
		commit('SET_GLOBAL_CSS', css)
	},

	/**
	 * Set global JS
	 */
	setGlobalJs({ commit }, js) {
		commit('SET_GLOBAL_JS', js)
	},

	/**
	 * Set fonts
	 */
	setFonts({ commit }, fonts) {
		commit('SET_FONTS', fonts)
	},

	/**
	 * Delete a section
	 */
	deleteSection({ commit }, sectionId) {
		commit('DELETE_SECTION', sectionId)
	},

	/**
	 * Reorder sections via drag and drop
	 */
	reorderSections({ commit }, { fromIndex, toIndex }) {
		commit('REORDER_SECTIONS', { fromIndex, toIndex })
	},

	/**
	 * Move section up
	 */
	moveSectionUp({ commit }, sectionId) {
		commit('MOVE_SECTION_UP', sectionId)
	},

	/**
	 * Move section down
	 */
	moveSectionDown({ commit }, sectionId) {
		commit('MOVE_SECTION_DOWN', sectionId)
	},

	/**
	 * Add messages to a section's conversation history
	 */
	addToConversation({ commit }, { sectionId, messages }) {
		commit('ADD_TO_CONVERSATION', { sectionId, messages })
	},

	/**
	 * Set the active section
	 */
	setActiveSection({ commit }, sectionId) {
		commit('SET_ACTIVE_SECTION', sectionId)
	},

	/**
	 * Update page metadata
	 */
	updatePageMetadata({ commit }, metadata) {
		commit('SET_PAGE_METADATA', metadata)
	},

	/**
	 * Set generation loading state
	 */
	setGenerating({ commit }, value) {
		commit('SET_IS_GENERATING', value)
		if (value) {
			commit('SET_GENERATION_ERROR', null)
		}
	},

	/**
	 * Set generation error
	 */
	setGenerationError({ commit }, error) {
		commit('SET_GENERATION_ERROR', error)
		commit('SET_IS_GENERATING', false)
	},

	/**
	 * Mark changes as saved
	 */
	markAsSaved({ commit }) {
		commit('SET_UNSAVED_CHANGES', false)
	},

	/**
	 * Clear all data
	 */
	clearAll({ commit }) {
		commit('CLEAR_ALL')
	}
}

const getters = {
	/**
	 * Get all sections
	 */
	getSections: (state) => state.sections,

	/**
	 * Get a specific section by ID
	 */
	getSectionById: (state) => (id) => {
		return state.sections.find((s) => s.id === id)
	},

	/**
	 * Get the active section
	 */
	getActiveSection: (state) => {
		if (!state.activeSectionId) return null
		return state.sections.find((s) => s.id === state.activeSectionId)
	},

	/**
	 * Get section index by ID
	 */
	getSectionIndex: (state) => (id) => {
		return state.sections.findIndex((s) => s.id === id)
	},

	/**
	 * Get page metadata
	 */
	getPageMetadata: (state) => state.pageMetadata,

	/**
	 * Check if generation is in progress
	 */
	isGenerating: (state) => state.isGenerating,

	/**
	 * Get generation error
	 */
	getGenerationError: (state) => state.generationError,

	/**
	 * Check for unsaved changes
	 */
	hasUnsavedChanges: (state) => state.unsavedChanges,

	/**
	 * Get section count
	 */
	sectionCount: (state) => state.sections.length,

	/**
	 * Check if a section can move up
	 */
	canMoveUp: (state) => (sectionId) => {
		const index = state.sections.findIndex((s) => s.id === sectionId)
		return index > 0
	},

	/**
	 * Check if a section can move down
	 */
	canMoveDown: (state) => (sectionId) => {
		const index = state.sections.findIndex((s) => s.id === sectionId)
		return index !== -1 && index < state.sections.length - 1
	},

	/**
	 * Get combined HTML of all sections (for preview/export)
	 */
	getCombinedHTML: (state) => {
		return state.sections.map((s) => s.html).join('\n')
	},

	/**
	 * Get combined CSS of all sections
	 */
	getCombinedCSS: (state) => {
		return state.sections
			.filter((s) => s.css)
			.map((s) => s.css)
			.join('\n')
	},

	/**
	 * Get combined JS of all sections
	 */
	getCombinedJS: (state) => {
		return state.sections
			.filter((s) => s.js)
			.map((s) => s.js)
			.join('\n')
	},

	/**
	 * Get global CSS
	 */
	getGlobalCss: (state) => state.globalCss,

	/**
	 * Get global JS
	 */
	getGlobalJs: (state) => state.globalJs,

	/**
	 * Get fonts
	 */
	getFonts: (state) => state.fonts
}

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters
}
